/**
 * HDH gRPC-Web Service Client
 * 
 * Provides ValidateCalculatorParams method for TYPO3
 * Uses window.HDH_API_BASE_URL for API endpoint
 */

import { grpc } from '@improbable-eng/grpc-web';

// API Base URL from TYPO3 configuration
declare global {
  interface Window {
    HDH_API_BASE_URL?: string;
  }
}

function getApiBaseUrl(): string {
  return window.HDH_API_BASE_URL || '/dsh/api/v1';
}

// Status codes matching the protobuf enum
export enum StatusCode {
  Unknown = 0,
  Ok = 1,
  ErrorAgeInvalid = 2,
  ErrorAgeInvalidTooLow = 3,
  ErrorAgeInvalidTooHigh = 4,
  Error = 5
}

// Request/Response interfaces
export interface ValidateCalculatorParamsRequest {
  selectedAgentVid?: string;
  vid?: string;
  iframeId?: string;
  isRedirected?: boolean;
  isIframe?: boolean;
}

export interface ValidateCalculatorParamsResponse {
  statusCode: StatusCode;
  vid?: string;
  iframeId?: string;
  isRedirected?: boolean;
  isIframe?: boolean;
  gtagId?: string;
}

// Protobuf field numbers for encoding
const FIELD_SELECTED_AGENT_VID = 1;
const FIELD_VID = 2;
const FIELD_IFRAME_ID = 3;
const FIELD_IS_REDIRECTED = 4;
const FIELD_IS_IFRAME = 5;

// Simple protobuf encoder for ValidateCalculatorParamsRequest
function encodeRequest(request: ValidateCalculatorParamsRequest): Uint8Array {
  const parts: number[] = [];
  
  if (request.selectedAgentVid) {
    const bytes = new TextEncoder().encode(request.selectedAgentVid);
    parts.push((FIELD_SELECTED_AGENT_VID << 3) | 2); // wire type 2 = length-delimited
    parts.push(bytes.length);
    bytes.forEach(b => parts.push(b));
  }
  
  if (request.vid) {
    const bytes = new TextEncoder().encode(request.vid);
    parts.push((FIELD_VID << 3) | 2);
    parts.push(bytes.length);
    bytes.forEach(b => parts.push(b));
  }
  
  if (request.iframeId) {
    const bytes = new TextEncoder().encode(request.iframeId);
    parts.push((FIELD_IFRAME_ID << 3) | 2);
    parts.push(bytes.length);
    bytes.forEach(b => parts.push(b));
  }
  
  if (request.isRedirected !== undefined) {
    parts.push((FIELD_IS_REDIRECTED << 3) | 0); // wire type 0 = varint
    parts.push(request.isRedirected ? 1 : 0);
  }
  
  if (request.isIframe !== undefined) {
    parts.push((FIELD_IS_IFRAME << 3) | 0);
    parts.push(request.isIframe ? 1 : 0);
  }
  
  return new Uint8Array(parts);
}

// Simple protobuf decoder for ValidateCalculatorParamsResponse
function decodeResponse(data: Uint8Array): ValidateCalculatorParamsResponse {
  const response: ValidateCalculatorParamsResponse = {
    statusCode: StatusCode.Ok
  };
  
  let pos = 0;
  while (pos < data.length) {
    const tag = data[pos++];
    const fieldNumber = tag >> 3;
    const wireType = tag & 0x7;
    
    if (wireType === 0) {
      // Varint
      let value = 0;
      let shift = 0;
      while (pos < data.length) {
        const byte = data[pos++];
        value |= (byte & 0x7f) << shift;
        if ((byte & 0x80) === 0) break;
        shift += 7;
      }
      
      switch (fieldNumber) {
        case 1: response.statusCode = value as StatusCode; break;
        case 4: response.isRedirected = value === 1; break;
        case 5: response.isIframe = value === 1; break;
      }
    } else if (wireType === 2) {
      // Length-delimited (string)
      const length = data[pos++];
      const strBytes = data.slice(pos, pos + length);
      const str = new TextDecoder().decode(strBytes);
      pos += length;
      
      switch (fieldNumber) {
        case 2: response.vid = str; break;
        case 3: response.iframeId = str; break;
        case 6: response.gtagId = str; break;
      }
    }
  }
  
  return response;
}

// gRPC-Web frame encoding (5-byte header + message)
function frameMessage(data: Uint8Array): Uint8Array {
  const frame = new Uint8Array(5 + data.length);
  frame[0] = 0; // not compressed
  frame[1] = (data.length >> 24) & 0xff;
  frame[2] = (data.length >> 16) & 0xff;
  frame[3] = (data.length >> 8) & 0xff;
  frame[4] = data.length & 0xff;
  frame.set(data, 5);
  return frame;
}

// Parse gRPC-Web response
function parseGrpcWebResponse(data: Uint8Array): Uint8Array {
  if (data.length < 5) {
    throw new Error('Invalid gRPC-Web response: too short');
  }
  
  const compressed = data[0];
  const length = (data[1] << 24) | (data[2] << 16) | (data[3] << 8) | data[4];
  
  if (compressed !== 0) {
    throw new Error('Compressed responses not supported');
  }
  
  return data.slice(5, 5 + length);
}

/**
 * Validates calculator parameters with the backend
 * 
 * @param request The validation request
 * @returns Promise with the validation response
 */
export async function validateCalculatorParams(
  request: ValidateCalculatorParamsRequest
): Promise<ValidateCalculatorParamsResponse> {
  const url = `${getApiBaseUrl()}/dsh.calculator.Calculator/ValidateCalculatorParams`;
  
  const encodedRequest = encodeRequest(request);
  const framedRequest = frameMessage(encodedRequest);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/grpc-web+proto',
      'Accept': 'application/grpc-web+proto',
      'X-Grpc-Web': '1'
    },
    body: framedRequest
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`);
  }
  
  const responseData = await response.arrayBuffer();
  const messageData = parseGrpcWebResponse(new Uint8Array(responseData));
  
  return decodeResponse(messageData);
}

// Export for global access
export const HDHService = {
  validateCalculatorParams,
  StatusCode
};

// Make available globally
if (typeof window !== 'undefined') {
  (window as any).HDHService = HDHService;
}

